import {
    isPathSegment,
    isSeparator,
    type Options,
    parsePath,
    subPath
} from '../../../http/server/path.ts';

import { PathPatternParser } from './path-pattern-parser.ts';
import { PathElement } from './internal/path-element.ts';
import { SeparatorPathElement } from './internal/separator-path-element.ts';
import { WildcardPathElement } from './internal/wildcard-path-element.ts';
import { CaptureSegmentsPathElement } from './internal/capture-segments-path-element.ts';
import { WildcardSegmentsPathElement } from './internal/wildcard-segments-path-element.ts';
import type { PathContainer, PathContainerElement } from '../../../../types/path';

export /* internal */ class MatchingContext {
    readonly #candidate: PathContainer;
    readonly pathElements: readonly PathContainerElement[];
    readonly pathLength: number;
    #extractedUriVariables?: Map<string, string>;
    #extractedMatrixVariables?: Map<string, ReadonlyMap<string, readonly string[]>>;
    readonly extractingVariables: boolean = false;
    #determineRemainingPath: boolean = false;

    remainingPathIndex: number = 0;

    constructor(pathContainer: PathContainer, extractVariables: boolean) {
        this.#candidate = pathContainer;
        this.pathElements = pathContainer.elements;
        this.pathLength = this.pathElements.length;
        this.extractingVariables = extractVariables;
    }

    get determinedRemainingPath(): boolean {
        return this.#determineRemainingPath;
    }

    setMatchAllowExtraPath() {
        this.#determineRemainingPath = true;
    }


    set(key: string, value: string, parameters: ReadonlyMap<string, readonly string[]>) {
        if (this.#extractedUriVariables === undefined) {
            this.#extractedUriVariables = new Map();
        }
        this.#extractedUriVariables.set(key, value);

        if (parameters.size > 0) {
            if (this.#extractedMatrixVariables === undefined) {
                this.#extractedMatrixVariables = new Map();
            }
            this.#extractedMatrixVariables.set(key, parameters);
        }
    }

    get pathMatchResult() {
        if (this.#extractedUriVariables === undefined) {
            return PathMatchInfo.EMPTY;
        }
        else {
            return new PathMatchInfo(this.#extractedUriVariables, this.#extractedMatrixVariables);
        }
    }

    /*internal*/
    isSeparator(pathIndex: number): boolean {
        return isSeparator(this.pathElements[pathIndex]);
    }

    /*internal*/
    pathElementValue(pathIndex: number): string {
        const element = (pathIndex < this.pathLength) ? this.pathElements[pathIndex] : null;
        if (element !== null && isPathSegment(element)) {
            return element.valueToMatch;
        }
        else {
            return '';
        }
    }
}

export class PathMatchInfo {
    static readonly EMPTY = new PathMatchInfo(new Map(), new Map());

    readonly uriVariables: ReadonlyMap<string, string>;
    readonly matrixVariables: ReadonlyMap<string, ReadonlyMap<string, readonly string[]>>;

    constructor(uriVars: ReadonlyMap<string, string>, matrixVars?: ReadonlyMap<string, ReadonlyMap<string, readonly string[]>>) {
        this.uriVariables = uriVars;
        this.matrixVariables = matrixVars ?? new Map();
    }

    toString(): string {
        return `PathMatchInfo(uriVariables=${this.uriVariables}, matrixVariables=${this.matrixVariables})`;
    }
}

export class PathPattern {
    static readonly #EMPTY_PATH = parsePath('');
    static readonly SPECIFICITY_COMPARATOR = (a: PathPattern, b: PathPattern): number => {
        if (a === null || a === undefined) {
            return (b === null || b === undefined) ? 0 : 1;
        }
        else if (b === null || b === undefined) {
            return -1;
        }
        else {
            // catchAll true > catchAll false
            if (a.catchAll && !b.catchAll) {
                return 1;
            }
            else if (!a.catchAll && b.catchAll) {
                return -1;
            }
            else {
                // todo
                return 0;
            }
        }
    }

    readonly #patternString: string;
    readonly #parser: PathPatternParser;
    readonly #pathOptions: Options;
    readonly #caseSensitive: boolean;
    readonly #head?: PathElement;

    #capturedVariableCount: number = 0;
    #normalizedLength: number = 0;
    #endsWithSeparatorWildcard: boolean = false;
    #score: number = 0;
    // does the pattern ends with {*...}.
    #catchAll: boolean = false;

    constructor(patternText: string, parser: PathPatternParser, head?: PathElement) {
        this.#patternString = patternText;
        this.#parser = parser;
        this.#pathOptions = parser.pathOptions;
        this.#caseSensitive = parser.caseSensitive;
        this.#head = head;

        // Compute fields for fast comparison
        let current = head;
        while (current !== undefined) {
            this.#capturedVariableCount += current.captureCount;
            this.#normalizedLength += current.normalizedLength;
            this.#score += current.score;
            if (current instanceof CaptureSegmentsPathElement || current instanceof WildcardSegmentsPathElement) {
                this.#catchAll = true;
            }
            if (current instanceof SeparatorPathElement && current.next instanceof WildcardPathElement && current.next.next === undefined) {
                this.#endsWithSeparatorWildcard = true;
            }

            current = current.next;
        }
    }

    get patternString(): string {
        return this.#patternString;
    }

    get hasPatternSyntax(): boolean {
        return (this.#score > 0 || this.#catchAll || this.#patternString.indexOf('?') !== -1);
    }

    #hasLength(container?: PathContainer): container is PathContainer {
        return container !== undefined && container.elements.length > 0;
    }

    #pathContainerIsJustSeparator(container: PathContainer): boolean {
        return container.value.length === 1 && container.value.charCodeAt(0) === this.separator;
    }

    matches(pathContainer?: PathContainer): boolean {
        if (this.#head === undefined) {
            return !this.#hasLength(pathContainer);
        }
        else if (!this.#hasLength(pathContainer)) {
            if (this.#head instanceof WildcardSegmentsPathElement || this.#head instanceof CaptureSegmentsPathElement) {
                pathContainer = PathPattern.#EMPTY_PATH; // will allow CaptureTheRest to bind the variable to empty
            }
            else {
                return false;
            }
        }
        const matchingContext = new MatchingContext(pathContainer, false);
        return this.#head.matches(0, matchingContext);
    }

    matchAndExtract(pathContainer: undefined | PathContainer): PathMatchInfo | undefined {
        if (this.#head === undefined) {
            return (this.#hasLength(pathContainer) && !this.#pathContainerIsJustSeparator(pathContainer)) ? undefined : PathMatchInfo.EMPTY;
        }
        else if (!this.#hasLength(pathContainer)) {
            if (this.#head instanceof WildcardSegmentsPathElement || this.#head instanceof CaptureSegmentsPathElement) {
                pathContainer = PathPattern.#EMPTY_PATH; // will allow CaptureTheRest to bind the variable to empty
            }
            else {
                return undefined;
            }
        }
        const matchingContext = new MatchingContext(pathContainer, true);
        return this.#head.matches(0, matchingContext) ? matchingContext.pathMatchResult : undefined;
    }


    extractPathWithinPattern(path: PathContainer): PathContainer {
        const pathElements = path.elements;
        const pathElementsLength = pathElements.length;

        let startIndex = 0;

        let elem = this.#head;
        while (elem !== undefined) {
            if (!elem.literal) {
                break;
            }
            elem = elem.next;
            startIndex++;
        }
        if (elem === undefined) {
            // there is no pattern piece
            return parsePath('')
        }

        // skip leading separators that would be in the result
        while (startIndex < pathElementsLength && isSeparator(pathElements[startIndex])) {
            startIndex++;
        }

        let endIndex = pathElementsLength;
        // skip trailing separators that would be in the result
        while (endIndex > 0 && isSeparator(pathElements[endIndex - 1])) {
            endIndex--;
        }

        let multipleAdjacentSeparators = false;
        for (let i = startIndex; i < endIndex - 1; i++) {
            if (isSeparator(pathElements[i]) && isSeparator(pathElements[i + 1])) {
                multipleAdjacentSeparators = true;
                break;
            }
        }

        let resultPath: PathContainer;
        if (multipleAdjacentSeparators) {
            const sb : string[] = [];
            let  i = startIndex;
            while (i < endIndex) {
                const e = pathElements[i++];
                sb.push(e.value);
                if (isSeparator(e)) {
                    while (i < endIndex && isSeparator(pathElements[i])) {
                        i++;
                    }
                }
            }
            resultPath = parsePath(sb.join(''), this.#pathOptions);
        }
        else if (startIndex >= endIndex) {
            resultPath = parsePath('', this.#pathOptions);
        }
        else {
            resultPath = subPath(path, startIndex, endIndex);
        }
        return resultPath;
    }

    /* internal */
    get score(): number {
        return this.#score;
    }

    /* internal */
    get catchAll(): boolean {
        return this.#catchAll;
    }

    /* internal */
    get normalizedLength(): number {
        return this.#normalizedLength;
    }

    /* internal */
    get separator(): number {
        return this.#pathOptions.separator;
    }

    /* internal */
    get capturedVariableCount(): number {
        return this.#capturedVariableCount;
    }

    /* internal */
    toChainString(): string {
        const sb: string[] = [];
        let pe = this.#head;
        while (pe !== undefined) {
            sb.push(pe.toString());
            pe = pe.next;
        }
        return sb.join(' ');
    }

    /* internal */
    computePatternString(): string {
        const sb: string[] = [];
        let current = this.#head;
        while (current !== undefined) {
            sb.push(String.fromCharCode(...(current.chars)));
            current = current.next;
        }
        return sb.join('');
    }

    /* internal */
    get head(): PathElement | undefined {
        return this.#head;
    }

}
